import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { BlockRenderer } from './BlockRenderer.js';
import { NarratedAutoWalkProgress, type NarratedAutoWalkStep } from './NarratedAutoWalkProgress.js';
import { ReturnPage } from './ReturnPage.js';
import {
  type CheckoutBlockDto,
  type CheckoutConfigDto,
  type CheckoutDecisionConditionDto,
  type CheckoutFlowDto,
  type CheckoutNodeDto,
  type CheckoutSessionDto,
  type WaitTickResponse,
  baseUrl,
  createSession,
  getFlowState,
  pickLocale,
  putFlowState,
  runTask,
  tickWait,
} from './api.js';
import { legacyPageToBlocks } from './legacy-page-to-blocks.js';
import { renderPageNode } from './renderPageNode.js';
import type { RiskSessionResponse } from './risk-collect.js';
import { riskGateForNode } from './risk-gate.js';
import { usePacedAutoWalk } from './usePacedAutoWalk.js';
import { buildIntegrationScopeEntry } from './util/integration-scope.js';

/**
 * Client-side runtime for operator-authored CheckoutFlow graphs.
 *
 * The runner walks the graph starting at the trigger node, maintaining a
 * scope object that downstream nodes read from:
 *
 *   scope = {
 *     validator_inputs:  { [fieldId]: string },   // from page nodes
 *     integration:       { [validatorId]: { ...lockedFields, metadata } },
 *     tenant_id:         string,
 *     now_iso:           string,
 *   }
 *
 * The runner is fail-loud: any unhandled state surfaces an error and stops
 * the flow (rather than silently falling through to a hardcoded path).
 *
 * Three Cs in action:
 *   - Configurable: every render decision comes from the operator's graph;
 *     the runner has zero tenant-specific code.
 *   - Composable: each node kind is handled by a dedicated branch — swapping
 *     `integration` for a different validator runtime is a one-file change.
 *   - Conversational: the scope object is structured JSON an LLM agent can
 *     summarise ("the flow asked for the reference number, then ran the
 *     AVITS lookup, then …").
 */

/** Run-time scope visible to decisions + integrations. */
interface FlowScope {
  validator_inputs: Record<string, string>;
  integration: Record<string, Record<string, unknown>>;
  tenant_id: string;
  now_iso: string;
}

interface RunnerProps {
  config: CheckoutConfigDto;
  flow: CheckoutFlowDto;
  tenant: string;
  strategy: string;
  locale: string;
  selectedMethod: string | null;
  risk: RiskSessionResponse | null;
  riskError: string | null;
  /**
   * Operator-authored help text rendered under the lookup-style form. Threaded
   * through from `display.referenceHelpText` so operators authoring it in the
   * strategy editor see it appear in the runner-driven checkout — not only on
   * the legacy hardcoded `Lookup` widget.
   */
  referenceHelpText?: string;
  /**
   * Called when the runner reaches `redirect_hpp` and successfully opens a
   * `CheckoutSession`. The parent (App.tsx) lifts that session into its own
   * state; from that point the BlockRenderer slot map switches the
   * lookup-form slot to `ConfirmPanel`, and the runner stops rendering. The
   * runner is responsible ONLY for the pre-session graph walk.
   */
  onSessionReady?: (s: CheckoutSessionDto) => void;
  /**
   * Called when the flow scope's `validator_inputs.currency` is set (by a
   * `set_variable` / `expression` node). Lets App.tsx filter the payment-method
   * tiles by the flow-set currency before the session exists (bd
   * currency-aware-payment-methods).
   */
  onCurrency?: (currency: string) => void;
  /**
   * bd 6z0 — URL-derived prefill computed in App.tsx via applyMappings().
   * The runner seeds validator_inputs from this so a lookup-form node bound
   * to `validator_inputs.referenceNumber` renders pre-filled. Without this
   * the flow runner silently ignored operator-authored queryParamMappings.
   */
  prefill?: import('./prefill.js').LookupPrefill;
}

/**
 * bd 6z0 — Pure helper that builds the runner's initial scope. Lives at
 * module scope (not inside the component) so its branches are testable
 * without rendering. The runner calls this from its useState initializer.
 */
export function initialScopeFromPrefill(
  tenant: string,
  prefill: import('./prefill.js').LookupPrefill | undefined,
  nowIso: string,
): FlowScope {
  const validator_inputs: Record<string, string> = {};
  if (prefill?.reference && prefill.reference.length > 0) {
    validator_inputs.referenceNumber = prefill.reference;
  }
  if (prefill?.email && prefill.email.length > 0) {
    validator_inputs.email = prefill.email;
  }
  if (prefill?.name && prefill.name.length > 0) {
    validator_inputs.name = prefill.name;
  }
  return {
    validator_inputs,
    integration: {},
    tenant_id: tenant,
    now_iso: nowIso,
  };
}

/** Maximum unique node-id visits in one runner mount; a safety net even when
 *  server-side cycle detection rejects loops at save time. */
const RUNNER_VISIT_LIMIT = 128;

/**
 * Resolve a dotted token (e.g. `validator_inputs.referenceNumber`) against
 * the scope. Returns `undefined` when any segment is missing. Pure helper —
 * tests cover the comparator + token resolution in `compileCondition`.
 */
function resolveToken(scope: FlowScope, token: string): unknown {
  if (!token) return undefined;
  const parts = token.split('.');
  let cursor: unknown = scope;
  for (const p of parts) {
    if (cursor == null || typeof cursor !== 'object') return undefined;
    cursor = (cursor as Record<string, unknown>)[p];
  }
  return cursor;
}

/**
 * Compile a visual `CheckoutDecisionCondition` into a pure boolean function.
 * Exported so the inspector can preview the truthy/falsy outcome during
 * authoring without spinning up the full runner.
 */
export function compileCondition(c: CheckoutDecisionConditionDto): (scope: FlowScope) => boolean {
  const right = c.rightValue ?? '';
  switch (c.comparator) {
    case 'equals':
      return (s) => String(resolveToken(s, c.leftToken) ?? '') === right;
    case 'not_equals':
      return (s) => String(resolveToken(s, c.leftToken) ?? '') !== right;
    case 'contains':
      return (s) => String(resolveToken(s, c.leftToken) ?? '').includes(right);
    case 'starts_with':
      return (s) => String(resolveToken(s, c.leftToken) ?? '').startsWith(right);
    case 'greater_than': {
      const r = Number(right);
      return (s) => {
        const v = Number(resolveToken(s, c.leftToken));
        return Number.isFinite(v) && Number.isFinite(r) && v > r;
      };
    }
    case 'less_than': {
      const r = Number(right);
      return (s) => {
        const v = Number(resolveToken(s, c.leftToken));
        return Number.isFinite(v) && Number.isFinite(r) && v < r;
      };
    }
    case 'in': {
      const list = right.split(',').map((x) => x.trim());
      return (s) => list.includes(String(resolveToken(s, c.leftToken) ?? ''));
    }
    case 'not_in': {
      const list = right.split(',').map((x) => x.trim());
      return (s) => !list.includes(String(resolveToken(s, c.leftToken) ?? ''));
    }
    case 'is_empty':
      return (s) => {
        const v = resolveToken(s, c.leftToken);
        return v == null || v === '';
      };
    case 'is_not_empty':
      return (s) => {
        const v = resolveToken(s, c.leftToken);
        return v != null && v !== '';
      };
    default:
      // Defensive: an operator should never reach this — the comparator union
      // is exhaustive — but if a new kind lands in the schema before the
      // runner, fail loud rather than silently false.
      return () => {
        throw new Error(`Unknown comparator: ${(c as { comparator: string }).comparator}`);
      };
  }
}

/** Find the next edge from a node, optionally matching a label. */
function nextEdge(
  flow: CheckoutFlowDto,
  fromId: string,
  label?: 'true' | 'false' | 'success' | 'failure',
): string | null {
  const edges = flow.edges.filter((e) => e.from === fromId);
  if (label) {
    const labelled = edges.find((e) => e.label === label);
    return labelled?.to ?? null;
  }
  // Unlabelled traversal: take the first (and conventionally only) edge.
  return edges[0]?.to ?? null;
}

function getNode(flow: CheckoutFlowDto, id: string | null): CheckoutNodeDto | null {
  if (!id) return null;
  return flow.nodes.find((n) => n.id === id) ?? null;
}

/**
 * Build the {completed, running, pending} step lists shown by
 * `NarratedAutoWalkProgress`. Walks forward from `current` until it
 * hits the next page node (stopping point) or the end of the graph.
 *
 * `completed` is empty in this pass — the runner advances the
 * `current` node when each step finishes, so the "in-flight" view
 * naturally shows the active step as `running` and the rest as
 * `pending`. A richer history could be added in a follow-up.
 */
/** Node kinds the flow walks through server-side (no shopper interaction).
 *  These are the steps the paced auto-walk narrates; everything else (page,
 *  wait, redirect_hpp, result …) is a destination that ends the walk. */
const AUTO_WALK_KINDS = new Set(['integration', 'decision', 'expression', 'parallel']);

function computeAutoWalkSteps(
  flow: CheckoutFlowDto,
  current: CheckoutNodeDto,
  locale: string,
): {
  completed: NarratedAutoWalkStep[];
  running: NarratedAutoWalkStep | null;
  pending: NarratedAutoWalkStep[];
} {
  const ids: string[] = [];
  let nodeId: string | undefined = current.id;
  let safety = 64;
  while (nodeId && safety-- > 0) {
    const node = flow.nodes.find((n) => n.id === nodeId);
    if (!node) break;
    if (node.kind === 'page' && node.id !== current.id) break;
    ids.push(node.id);
    const edge = flow.edges.find((e) => e.from === nodeId);
    nodeId = edge?.to;
  }
  const steps: NarratedAutoWalkStep[] = ids.map((id) => {
    const node = flow.nodes.find((n) => n.id === id);
    const kind = node?.kind ?? 'integration';
    const narrationMap = readNarration(node);
    return {
      nodeId: id,
      narration: (narrationMap ? pickLocale(narrationMap, locale) : '') || defaultNarration(kind),
    };
  });
  return {
    completed: [],
    running: steps[0] ?? null,
    pending: steps.slice(1),
  };
}

/**
 * Per-node narration lives under the kind-specific config object on the
 * node. This extracts it without leaning on `as any`.
 */
function readNarration(node: CheckoutNodeDto | undefined): Record<string, string> | undefined {
  if (!node) return undefined;
  // The DTO config types are tight; cast each carrier to a loose shape
  // that just probes `narration`. Reason: `narration` is a freshly-added
  // optional field that not every checkout-side DTO mirrors yet.
  const probe = (cfg: unknown): Record<string, string> | undefined => {
    if (!cfg || typeof cfg !== 'object') return undefined;
    const n = (cfg as { narration?: unknown }).narration;
    return n && typeof n === 'object' ? (n as Record<string, string>) : undefined;
  };
  return (
    probe(node.integration) ??
    probe(node.expression) ??
    probe(node.decision) ??
    probe(node.wait) ??
    probe((node as { parallel?: unknown }).parallel)
  );
}

function defaultNarration(kind: string): string {
  switch (kind) {
    case 'integration':
      return 'Verifying…';
    case 'expression':
      return 'Computing…';
    case 'decision':
      return 'Choosing the right path…';
    case 'parallel':
      return 'Running checks in parallel…';
    case 'wait':
      return 'Waiting for confirmation…';
    default:
      return 'Working…';
  }
}

/**
 * Walk forward from `current` to the next page node. Returns its
 * normalised block list so the loading state can render a skeleton
 * placeholder for the up-next view.
 */
function findNextPageBlocks(flow: CheckoutFlowDto, from: CheckoutNodeDto): CheckoutBlockDto[] {
  let nodeId: string | undefined = from.id;
  let safety = 64;
  // Skip the current node so we don't return its blocks.
  const edgeFromCurrent = flow.edges.find((e) => e.from === nodeId);
  nodeId = edgeFromCurrent?.to;
  while (nodeId && safety-- > 0) {
    const node = flow.nodes.find((n) => n.id === nodeId);
    if (!node) break;
    if (node.kind === 'page') {
      const normalized = legacyPageToBlocks(node.page ?? {});
      return normalized.blocks ?? [];
    }
    const edge = flow.edges.find((e) => e.from === nodeId);
    nodeId = edge?.to;
  }
  return [];
}

export function CheckoutFlowRunner(props: RunnerProps) {
  const {
    config,
    flow,
    tenant,
    strategy,
    locale,
    risk,
    riskError,
    referenceHelpText,
    onSessionReady,
    onCurrency,
    prefill,
  } = props;

  // Walk-state. The runner advances `currentNodeId` as the shopper completes
  // each step; integration + decision nodes call the SERVER (which is the
  // authority) and immediately advance to the next page or redirect.
  const [scope, setScope] = useState<FlowScope>(() =>
    initialScopeFromPrefill(tenant, prefill, new Date().toISOString()),
  );
  const [session, setSession] = useState<CheckoutSessionDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Surface the flow-set currency to the parent so it can filter method tiles
  // before the session exists (bd currency-aware-payment-methods).
  const flowCurrency = scope.validator_inputs.currency;
  useEffect(() => {
    if (flowCurrency) onCurrency?.(flowCurrency);
  }, [flowCurrency, onCurrency]);

  // Trigger is the entry node — start the flow there. If the graph is missing
  // a trigger we surface a fatal error rather than guess.
  const triggerNode = useMemo(() => flow.nodes.find((n) => n.kind === 'trigger') ?? null, [flow.nodes]);
  const [currentNodeId, setCurrentNodeId] = useState<string | null>(() => {
    if (!triggerNode) return null;
    return nextEdge(flow, triggerNode.id);
  });

  // Defence-in-depth cycle guard. Server-side `assertGraphIntegrity` rejects
  // cyclic graphs at PUT, but if a malformed graph somehow reaches the browser
  // we cap the number of visits per mount so the tab stays responsive.
  const visitCountRef = useRef(0);

  // === Durable runner state (S3-5) ===
  // On mount, hydrate currentNodeId + scope from the server. On every
  // advance, fire-and-forget a PUT. `hydrated` gates the persist effect so
  // we don't overwrite server state with the trigger default before the
  // initial GET resolves.
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const riskToken = risk?.token ?? null;
    const riskSessionId = risk?.session_id ?? null;
    if (!riskToken || !riskSessionId) {
      // No risk session yet — nothing to hydrate against. Mark hydrated so
      // the persist loop kicks in once the risk session arrives.
      setHydrated(true);
      return;
    }
    getFlowState({ tenant, sessionId: riskSessionId, riskToken })
      .then((state) => {
        if (cancelled) return;
        if (state && state.currentNodeId) {
          setCurrentNodeId(state.currentNodeId);
          setScope((prev) => ({
            ...prev,
            ...(state.scope as Partial<FlowScope>),
          }));
        }
        setHydrated(true);
      })
      .catch(() => {
        // Fail-soft: a hydrate failure should not block the shopper. Start
        // fresh at the trigger node.
        if (!cancelled) setHydrated(true);
      });
    return () => {
      cancelled = true;
    };
    // One-shot per (risk token / session) — re-hydrating mid-walk would
    // race the shopper.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [risk?.token, risk?.session_id]);

  useEffect(() => {
    if (!hydrated) return;
    const riskToken = risk?.token ?? null;
    const riskSessionId = risk?.session_id ?? null;
    if (!riskToken || !riskSessionId) return;
    // Fire-and-forget. A dropped persist only shortens the resume window.
    // `scope` is a closed shape (FlowScope) but the API type is a generic
    // JSON map; the server records it verbatim and the hydrate path narrows
    // it back to FlowScope.
    void putFlowState({
      tenant,
      sessionId: riskSessionId,
      riskToken,
      currentNodeId,
      // reason: server stores scope as opaque JSON; narrowing happens on
      // hydrate via the FlowScope shape.
      scope: scope as unknown as Record<string, unknown>,
    }).catch(() => {
      // Best-effort; the next advance will retry.
    });
  }, [hydrated, currentNodeId, scope, risk?.token, risk?.session_id, tenant]);

  const current = getNode(flow, currentNodeId);

  /**
   * Run integration + decision nodes when they become current. Pages +
   * redirect_hpp + result render in JSX below and do their own work on the
   * shopper's click. Integration + decision delegate to the SERVER — the
   * browser is not authoritative for either.
   */
  useEffect(() => {
    if (!current) return;
    let cancelled = false;

    visitCountRef.current += 1;
    if (visitCountRef.current > RUNNER_VISIT_LIMIT) {
      setError('Checkout flow exceeded the maximum number of steps.');
      return;
    }

    // Risk-session gate (bd replay-token-unavailable). Server-side nodes need a
    // resolved risk session. While it's still opening we WAIT (show busy) and
    // re-run when it arrives (risk is in this effect's deps) — erroring here
    // immediately is what broke checkout when a server node ran before the
    // session resolved. Only a genuine risk failure surfaces an error.
    const gate = riskGateForNode(current.kind, risk, riskError);
    if (gate.state === 'error') {
      setError(gate.message);
      return;
    }
    if (gate.state === 'wait') {
      setBusy(true);
      return;
    }
    // gate === 'ready': for server kinds the token + session are guaranteed.
    const riskToken = risk?.token ?? '';
    const riskSubject = risk?.session_id ?? '';

    if (current.kind === 'integration') {
      const cfg = current.integration;
      if (!cfg) {
        setError(`Integration node ${current.id} is missing config.`);
        return;
      }
      setBusy(true);
      runIntegration({
        tenant,
        strategy,
        nodeId: current.id,
        validatorInputs: scope.validator_inputs,
        riskToken,
        riskSessionId: riskSubject,
      })
        .then((result) => {
          if (cancelled) return;
          const nextScope: FlowScope = {
            ...scope,
            integration: {
              ...scope.integration,
              // Presentation projection (bd 2yi): expose ONLY operator-declared
              // mapped fields (+ structural ok/amount), never the raw upstream
              // metadata. This bounds what `{{ scope.integration.<id>.* }}`
              // tokens — on the page AND in the server-side bedrock prompt,
              // which reads the scope we send back — can reach.
              [cfg.validatorId]: buildIntegrationScopeEntry(result),
            },
          };
          setScope(nextScope);
          setBusy(false);
          // B-7: prefer the server-authoritative `nextNodeId` when the
          // response carried one. `undefined` ≡ legacy server (no field);
          // `null` ≡ server resolved to "stop here". The local
          // `nextEdge` fallback preserves back-compat with a deploy where
          // the API hasn't shipped B-7 yet.
          if (result.ok) {
            if (result.nextNodeId !== undefined) {
              if (result.nextNodeId) {
                setCurrentNodeId(result.nextNodeId);
                return;
              }
              setError(result.message ?? 'No success edge from this node.');
              return;
            }
            const to = nextEdge(flow, current.id, 'success') ?? nextEdge(flow, current.id);
            setCurrentNodeId(to);
            return;
          }
          const failureMode = cfg.onFailure ?? 'take_failure_edge';
          if (failureMode === 'take_failure_edge') {
            if (result.nextNodeId !== undefined) {
              if (result.nextNodeId) {
                setCurrentNodeId(result.nextNodeId);
                return;
              }
            } else {
              const to = nextEdge(flow, current.id, 'failure');
              if (to) {
                setCurrentNodeId(to);
                return;
              }
            }
          }
          setError(result.message ?? 'Integration failed.');
        })
        .catch((e: Error) => {
          if (cancelled) return;
          setBusy(false);
          setError(e.message);
        });
      return () => {
        cancelled = true;
      };
    }

    if (current.kind === 'decision') {
      if (!current.decision) {
        setError(`Decision node ${current.id} is missing config.`);
        return;
      }
      setBusy(true);
      evaluateDecision({
        tenant,
        strategy,
        nodeId: current.id,
        scope,
        riskToken,
        riskSessionId: riskSubject,
      })
        .then((branch) => {
          if (cancelled) return;
          setBusy(false);
          const to = nextEdge(flow, current.id, branch);
          if (!to) {
            setError(`Decision node ${current.id} has no '${branch}' edge.`);
            return;
          }
          setCurrentNodeId(to);
        })
        .catch((e: Error) => {
          if (cancelled) return;
          setBusy(false);
          setError(e.message);
        });
      return () => {
        cancelled = true;
      };
    }

    if (current.kind === 'expression' || current.kind === 'bedrock_prompt') {
      // Generic server-side Task dispatch (Sprint B-1 expression +
      // Sprint H bedrock_prompt). The server reads scope, runs the
      // executor (sandboxed JS for expression; AWS Bedrock for
      // bedrock_prompt), returns scopePatch + nextNodeId.
      setBusy(true);
      runTask({
        tenant,
        strategy,
        sessionId: riskSubject,
        nodeId: current.id,
        scope: scope as unknown as Record<string, unknown>,
        riskToken,
      })
        .then((result) => {
          if (cancelled) return;
          setBusy(false);
          // Merge scopePatch + any direct output into local scope so
          // downstream nodes (decisions, page templates) can read it.
          const merged = { ...scope } as Record<string, unknown> & FlowScope;
          if (result.scopePatch) {
            for (const [k, v] of Object.entries(result.scopePatch)) {
              merged[k] = v;
            }
          }
          if (result.output) {
            for (const [k, v] of Object.entries(result.output)) {
              // Don't clobber a scopePatch entry — patch wins.
              if (merged[k] === undefined) merged[k] = v;
            }
          }
          setScope(merged as FlowScope);
          if (!result.nextNodeId) {
            setError(result.message ?? `Task node ${current.id} has no outgoing edge for its result.`);
            return;
          }
          setCurrentNodeId(result.nextNodeId);
        })
        .catch((e: Error) => {
          if (cancelled) return;
          setBusy(false);
          setError(e.message);
        });
      return () => {
        cancelled = true;
      };
    }
    return undefined;
    // Re-run on node change AND when the risk session resolves/fails — a
    // server node that became current while risk was still opening must
    // proceed once the token arrives (bd replay-token-unavailable). Scope is
    // captured fresh inside the effect body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNodeId, risk?.token, risk?.session_id, riskError]);

  /**
   * When a redirect_hpp node becomes current, open a CheckoutSession and hand
   * it up to the parent via `onSessionReady`. App.tsx swaps the lookup-form
   * slot to `ConfirmPanel` at that point — the runner stops being responsible
   * for the confirm UI.
   */
  useEffect(() => {
    if (!current || current.kind !== 'redirect_hpp') return;
    if (session) return;
    let cancelled = false;
    setBusy(true);
    createSession({
      tenant,
      strategy_name: strategy,
      validator_inputs: scope.validator_inputs,
    })
      .then((s) => {
        if (cancelled) return;
        setSession(s);
        setBusy(false);
        onSessionReady?.(s);
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setBusy(false);
          setError(e.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [current?.kind, scope, strategy, session, current, onSessionReady]);

  // === Paced auto-walk (anti-flicker, bd aa14) ===
  // Decouple what the shopper SEES from how fast the flow runs. While the
  // current node is a server-side auto-walk node, feed the live upcoming steps
  // into a pacer that holds each step >= minStepMs, accumulates completed steps,
  // and gates `reveal`. This keeps ONE panel mounted across node hops (no
  // remount flash) and prevents the content from swapping faster than a human
  // can read. The hook is called before any conditional return (Rules of Hooks).
  const minStepMs =
    (config.tenant as { checkoutLoading?: { minStepMs?: number } }).checkoutLoading?.minStepMs ?? 600;
  const autoWalking = current != null && AUTO_WALK_KINDS.has(current.kind);
  const liveAutoWalkSteps =
    autoWalking && current
      ? (() => {
          const w = computeAutoWalkSteps(flow, current, locale);
          return w.running ? [w.running, ...w.pending] : w.pending;
        })()
      : [];
  const paced = usePacedAutoWalk(liveAutoWalkSteps, autoWalking, minStepMs);

  // === Render branches ===

  if (!triggerNode) {
    return (
      <div class="p-4 border-l-4 border-error bg-error-container/40 text-on-error-container rounded-r">
        <p class="text-body-sm">Checkout flow is missing a trigger node.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div class="p-4 border-l-4 border-error bg-error-container/40 text-on-error-container rounded-r">
        <p class="text-body-sm font-semibold">We couldn't continue this checkout.</p>
        <p class="text-body-sm mt-2">{error}</p>
      </div>
    );
  }

  if (!current) {
    return (
      <div class="p-4 text-on-surface-variant">
        <p class="text-body-sm">Loading next step…</p>
      </div>
    );
  }

  // Paced auto-walk panel: ONE persistent panel that advances steps at a humane
  // pace (>= minStepMs) and stays mounted across node hops, so it never flickers
  // past the shopper. Shown until the pacer reveals the destination page.
  if (!paced.reveal && paced.running !== null) {
    return (
      <NarratedAutoWalkProgress
        completed={paced.completed}
        running={paced.running}
        pending={paced.pending}
        nextPageBlocks={findNextPageBlocks(flow, current)}
        locale={locale}
        minStepMs={minStepMs}
      />
    );
  }

  // Fallback loading panel for non-auto-walk busy states (e.g. redirect_hpp
  // session creation) that the pacer doesn't drive.
  if (busy) {
    const autoWalk = computeAutoWalkSteps(flow, current, locale);
    return (
      <NarratedAutoWalkProgress
        completed={autoWalk.completed}
        running={autoWalk.running}
        pending={autoWalk.pending}
        nextPageBlocks={findNextPageBlocks(flow, current)}
        locale={locale}
        minStepMs={minStepMs}
      />
    );
  }

  // Page nodes render through a single `renderPageNode` call. The helper
  // funnels every page through `legacyPageToBlocks` so the legacy
  // `layout` enum and the new block-canvas shape both feed the same
  // BlockRenderer-driven render path.
  if (current.kind === 'page') {
    const page = current.page;
    if (!page) {
      return (
        <div class="p-4 border-l-4 border-error bg-error-container/40 text-on-error-container rounded-r">
          <p class="text-body-sm">Page node {current.id} is missing config.</p>
        </div>
      );
    }
    const normalized = legacyPageToBlocks(page);
    const submitAction = normalized.submit?.action ?? 'advance';

    // `create_session` action is the legacy `confirm` page path — open a
    // session and hand it up to App.tsx which swaps the slot to
    // ConfirmPanel. Behaviour preserved verbatim from the old branch.
    if (submitAction === 'create_session') {
      if (!session) {
        return (
          <ConfirmGate
            tenant={tenant}
            strategy={strategy}
            scope={scope}
            onReady={(s) => {
              setSession(s);
              onSessionReady?.(s);
            }}
            onError={setError}
          />
        );
      }
      return (
        <div class="p-4 text-on-surface-variant">
          <p class="text-body-sm">Preparing confirmation…</p>
        </div>
      );
    }

    return renderPageNode(current, {
      locale,
      initialFormValues: scope.validator_inputs,
      helpText: referenceHelpText,
      fallbackSubmitLabel: pickLocale(config.strategy.display.submitLabel, locale) || 'Continue',
      // Thread the runtime scope so `markdown`, `paragraph`, `heading`,
      // and `callout` blocks can interpolate `{{ scope.foo }}` tokens
      // (bedrock-generated tips, integration outputs, etc.).
      scope: scope as unknown as Record<string, unknown>,
      onSubmit: (values) => {
        // Persist into scope; bridge the legacy `amentumReferenceNumber`
        // key today's session-create handler expects. Once the parallel
        // agent's `formFields` lands this bridge moves to the DynamicForm
        // submit handler.
        const inputs = { ...scope.validator_inputs, ...values };
        if (values.referenceNumber) {
          inputs.amentumReferenceNumber = values.referenceNumber;
        }
        setScope({ ...scope, validator_inputs: inputs });
        const to = nextEdge(flow, current.id);
        setCurrentNodeId(to);
      },
    });
  }

  if (current.kind === 'redirect_hpp') {
    // Session creation happens in the effect above; once `onSessionReady`
    // fires App.tsx swaps the slot to ConfirmPanel. Show a placeholder until
    // that swap occurs.
    return (
      <div class="p-4 text-on-surface-variant">
        <p class="text-body-sm">Preparing handoff…</p>
      </div>
    );
  }

  // Sprint G — wait/poll node. The runner mounts the operator-authored
  // `waitPageBlocks` via `BlockRenderer` and drives the polling loop:
  //
  //   1. Mount → POST /v1/checkout/flow/wait-tick (server seeds counter)
  //   2. `awaiting` → setTimeout(intervalMs); on fire, POST again.
  //   3. `done` → advance to `nextNodeId` (server-authoritative).
  //   4. `timeout` → follow `failure-WAIT_TIMEOUT` typed edge if wired,
  //      else wildcard `failure`. The server includes a `nextNodeId`
  //      when it can resolve one; we honour it verbatim.
  //
  // The setTimeout is re-established on EVERY mount (refresh, tab
  // restore). The server's persisted counter + `nextPollAt` is the
  // authority — the runner's timer is a polite suggestion that the
  // server can always override via the next response.
  if (current.kind === 'wait') {
    if (!current.wait) {
      return (
        <div class="p-4 border-l-4 border-error bg-error-container/40 text-on-error-container rounded-r">
          <p class="text-body-sm">Wait node {current.id} is missing config.</p>
        </div>
      );
    }
    return (
      <WaitNodeView
        node={current}
        flow={flow}
        tenant={tenant}
        strategy={strategy}
        locale={locale}
        risk={risk}
        scope={scope}
        onAdvance={(toNodeId) => {
          if (!toNodeId) {
            setError('Wait node has no outgoing edge to follow.');
            return;
          }
          setCurrentNodeId(toNodeId);
        }}
        onError={setError}
      />
    );
  }

  if (current.kind === 'result') {
    return <ReturnPage outcome="success" />;
  }

  // Fail-loud: the graph reached a kind we don't know how to render.
  return (
    <div class="p-4 border-l-4 border-error bg-error-container/40 text-on-error-container rounded-r">
      <p class="text-body-sm">Unsupported flow node kind: {current.kind}</p>
    </div>
  );
}

/**
 * Bridges the gap between a non-confirm page (e.g. a custom data-collection
 * step) and the confirm step. It POSTs to `/v1/checkout/sessions` to open a
 * session from the current scope.validator_inputs, then hands the session up
 * to the runner.
 */
function ConfirmGate(props: {
  tenant: string;
  strategy: string;
  scope: FlowScope;
  onReady: (s: CheckoutSessionDto) => void;
  onError: (m: string) => void;
}) {
  const { tenant, strategy, scope, onReady, onError } = props;
  useEffect(() => {
    let cancelled = false;
    createSession({
      tenant,
      strategy_name: strategy,
      validator_inputs: scope.validator_inputs,
    })
      .then((s) => {
        if (!cancelled) onReady(s);
      })
      .catch((e: Error) => {
        if (!cancelled) onError(e.message);
      });
    return () => {
      cancelled = true;
    };
    // Once per mount — re-opening on every scope change would race the user.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div class="p-4 text-on-surface-variant">
      <p class="text-body-sm">Preparing confirmation…</p>
    </div>
  );
}

/**
 * Public, shopper-facing integration runner. Server-side endpoint runs the
 * validator, resolves any `upg-secret://` refs against the tenant's CMK,
 * caches results by (riskSession, validator, inputs hash), and returns ONLY
 * the parsed output. The browser never sees the validator URL, headers, or
 * auth config.
 */
interface IntegrationResult {
  ok: boolean;
  failureCode?: string;
  message?: string;
  lockedFields?: {
    amount?: { value: bigint | string; currency: string };
    metadata: Record<string, unknown>;
  };
  /**
   * Server-applied `outputMapping` patch — present only when the integration
   * node carried a non-empty `outputMapping`. The runner merges it into
   * `scope.integration.<validatorId>` so downstream decision nodes can read
   * remapped values without recomputing JSONPath on the client.
   */
  scopePatch?: { outputMapping?: Record<string, unknown> };
  /**
   * B-7 — server-authoritative next-node id. When present, the runner
   * prefers this over its local edge resolution; falls back to the
   * legacy `nextEdge` lookup so back-deploys keep working. `null` means
   * "the operator hasn't wired a matching edge — surface the validator
   * message".
   */
  nextNodeId?: string | null;
}

async function runIntegration(args: {
  tenant: string;
  strategy: string;
  nodeId: string;
  validatorInputs: Record<string, unknown>;
  riskToken: string;
  riskSessionId: string;
}): Promise<IntegrationResult> {
  const url = `${baseUrl}/v1/checkout/flow/integration`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': args.tenant,
      'X-Replay-Token': args.riskToken,
    },
    credentials: 'include',
    body: JSON.stringify({
      session_id: args.riskSessionId,
      strategy_name: args.strategy,
      node_id: args.nodeId,
      validator_inputs: args.validatorInputs,
    }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    return {
      ok: false,
      message: (j as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`,
    };
  }
  const j = (await res.json()) as {
    result?: IntegrationResult;
    scopePatch?: { outputMapping?: Record<string, unknown> };
    nextNodeId?: string | null;
  };
  const out = j.result ?? { ok: false, message: 'Integration returned no result.' };
  if (j.scopePatch) out.scopePatch = j.scopePatch;
  // B-7: server-supplied target. Preserved as `undefined` when absent so
  // the runner can distinguish "server didn't compute" (fall back) from
  // "server said stop" (`null`).
  if ('nextNodeId' in j) out.nextNodeId = j.nextNodeId ?? null;
  return out;
}

/**
 * Sprint G — `wait` node renderer + driver. Renders the operator-authored
 * `waitPageBlocks` to the shopper through `BlockRenderer` (Sprint E) and
 * drives the polling loop against `POST /v1/checkout/flow/wait-tick`.
 *
 * Resume story: every mount (initial OR after refresh) posts immediately
 * to /wait-tick. The server's persisted counter + `nextPollAt` is the
 * authority. On `awaiting`, we set a `setTimeout` for `min(intervalMs,
 * 60_000)` to be conservative — the server's reply on the next tick can
 * always advance + cap regardless of when the timer fires.
 */
function WaitNodeView(props: {
  node: CheckoutNodeDto;
  flow: CheckoutFlowDto;
  tenant: string;
  strategy: string;
  locale: string;
  risk: RiskSessionResponse | null;
  /** Live runner scope — threaded so the wait page's blocks can resolve
   *  `{{ scope.* }}` tokens (e.g. the applicant name) instead of rendering
   *  them literally. */
  scope: FlowScope;
  onAdvance: (nextNodeId: string | null) => void;
  onError: (msg: string) => void;
}) {
  const { node, flow, tenant, strategy, locale, risk, scope, onAdvance, onError } = props;
  const waitCfg = node.wait!;
  // Cap the browser timer at 60s to be conservative. The server-side cap
  // (G-7) ensures the worst-case wall-clock is `maxAttempts * intervalMs`
  // regardless of what the browser does — the timer here is purely a
  // "when to ask the server again" hint.
  const BROWSER_TIMER_CAP_MS = 60_000;

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function loop(): Promise<void> {
      if (cancelled) return;
      const riskToken = risk?.token ?? null;
      const sessionId = risk?.session_id ?? null;
      if (!riskToken || !sessionId) {
        onError('Replay token unavailable — refresh and retry.');
        return;
      }
      let resp: WaitTickResponse;
      try {
        resp = await tickWait({
          tenant,
          strategy,
          sessionId,
          nodeId: node.id,
          riskToken,
        });
      } catch (err) {
        if (!cancelled) onError((err as Error).message);
        return;
      }
      if (cancelled) return;

      if (resp.status === 'done') {
        onAdvance(resp.nextNodeId ?? null);
        return;
      }
      if (resp.status === 'timeout' || resp.status === 'failed') {
        // Server already routed through pickNextNode — typed-catch
        // `failure-WAIT_TIMEOUT` wins over the wildcard `failure` when
        // both exist. `nextNodeId === null` means the operator didn't
        // wire a fallback — surface the message.
        if (resp.nextNodeId) {
          onAdvance(resp.nextNodeId);
          return;
        }
        onError(resp.message ?? 'Wait timed out and no fallback page is wired for this flow.');
        return;
      }

      // awaiting — schedule the next tick. Trust the server's
      // `nextPollAt` when present; fall back to `intervalMs`. Cap the
      // browser-side timer at BROWSER_TIMER_CAP_MS so a misconfigured
      // wait can't park the browser on a single timer forever.
      const intervalMs = resp.intervalMs ?? waitCfg.intervalMs;
      let waitMs: number;
      if (resp.nextPollAt) {
        const target = new Date(resp.nextPollAt).getTime();
        waitMs = Math.max(0, target - Date.now());
      } else {
        waitMs = intervalMs;
      }
      const capped = Math.min(waitMs, BROWSER_TIMER_CAP_MS);
      timer = setTimeout(() => {
        void loop();
      }, capped);
    }

    void loop();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Re-establish the loop only on identity changes — refreshing within
    // the same node id resumes against the server's counter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node.id, risk?.token, risk?.session_id, tenant, strategy]);

  // We deliberately ignore `flow` for rendering — `BlockRenderer` only
  // needs the operator-authored block list. Other slots (form-fields,
  // pay-button) are intentionally empty: a wait page is read-only.
  // Reading `flow` here keeps the linter from complaining about the
  // closed-over reference inside the effect body above.
  void flow;
  const blocks = (waitCfg.waitPageBlocks ?? []) as CheckoutBlockDto[];
  return (
    <div class="bg-surface-container-lowest border border-outline-variant rounded-xl p-4 sm:p-6 lg:p-8 shadow-sm space-y-3">
      <BlockRenderer
        blocks={blocks}
        locale={locale}
        slots={{}}
        scope={scope as unknown as Record<string, unknown>}
      />
    </div>
  );
}

/**
 * Public, shopper-facing decision evaluator. The SERVER re-derives the
 * branch from the same scope so a tampered DOM cannot force a routing
 * decision.
 */
async function evaluateDecision(args: {
  tenant: string;
  strategy: string;
  nodeId: string;
  scope: FlowScope;
  riskToken: string;
  riskSessionId: string;
}): Promise<'true' | 'false'> {
  const url = `${baseUrl}/v1/checkout/flow/decision`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Tenant-Id': args.tenant,
      'X-Replay-Token': args.riskToken,
    },
    credentials: 'include',
    body: JSON.stringify({
      session_id: args.riskSessionId,
      strategy_name: args.strategy,
      node_id: args.nodeId,
      scope: args.scope,
    }),
  });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error((j as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`);
  }
  const j = (await res.json()) as { branch?: 'true' | 'false' };
  if (j.branch !== 'true' && j.branch !== 'false') {
    throw new Error('Decision endpoint returned an unexpected branch.');
  }
  return j.branch;
}
